'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle2, Sparkles, User } from 'lucide-react';

// Emil: strong ease-out — starts fast, never ease-in
const EASE = [0.23, 1, 0.32, 1] as const;

// Word-by-word headline reveal
const headline = [
  { text: 'Non', primary: false },
  { text: 'hai', primary: false },
  { text: 'bisogno', primary: false },
  { text: 'di', primary: false },
  { text: 'un', primary: false },
  { text: 'software.', primary: true}
];

type StepType = 'free_text' | 'single_choice' | 'email';

interface StepOption {
  value: string;
  label: string;
}

interface StepData {
  stepId?: string;
  question?: string;
  type?: StepType;
  placeholder?: string;
  options?: StepOption[];
  required?: boolean;
  progress?: number;
  endOfConversation?: boolean;
  message?: string;
}

interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
}

import dynamic from 'next/dynamic';

const LiquidChrome = dynamic(() => import('./liquid-chrome'), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-black" />, // Lightweight placeholder
});

export default function ConversationalForm() {
  const OFFLINE_MODE = true;

  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStep, setCurrentStep] = useState<StepData | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalMessage, setFinalMessage] = useState<string | null>(null);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  // Helper per generare UUID sicuro o fallback
  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  // Initialize conversation
  const initChat = async () => {
    // Evita doppia inizializzazione in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;

    const newSessionId = generateId();
    setSessionId(newSessionId);

    // Set initial local state without calling server
    setCurrentStep({
      stepId: 'init',
      type: 'free_text',
      placeholder: "Ciao! Come posso aiutarti oggi?",
      question: "Ciao! Come posso aiutarti oggi?" // Hidden initially
    });
    setLoading(false);
  };

  useEffect(() => {
    initChat();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      scrollContainerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, typing, currentStep]);

  const handleNewStep = async (step: StepData) => {
    if (step.endOfConversation) {
      setFinalMessage(step.message || null);
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant', content: step.message || '' }]);
      setCompleted(true);
      // Call complete webhook
      await fetch('/api/mock-lead-qual', {
          method: 'POST',
          body: JSON.stringify({ action: 'complete', sessionId }),
      });
      return;
    }

    // Special handling for the very first step (start of conversation)
    // We want to show the question as the placeholder/UI state, not as a bubble yet
    if (messages.length === 0 && !step.endOfConversation) {
      setCurrentStep(step);
      return;
    }

    setTyping(true);
    // Simulate typing delay based on text length
    setTimeout(() => {
      setTyping(false);
      if (step.question) {
        setMessages(prev => [...prev, { id: generateId(), role: 'assistant', content: step.question! }]);
      }
      setCurrentStep(step);
    }, 800);
  };

  const handleAnswer = async (answerValue: string, answerLabel?: string) => {
    if (!currentStep) return;

    // Add user message
    const displayAnswer = answerLabel || answerValue;
    setMessages(prev => [...prev, { id: generateId(), role: 'user', content: displayAnswer }]);
    setInputValue('');
    setCurrentStep(null); // Hide input while loading
    setLoading(true);

    try {
      const res = await fetch('/api/mock-lead-qual', {
        method: 'POST',
        body: JSON.stringify({
          action: 'next',
          sessionId,
          stepId: currentStep.stepId,
          step: messages.filter(m => m.role === 'user').length + 1,
          answer: answerValue
        }),
      });
      const nextStep = await res.json();
      handleNewStep(nextStep);
    } catch (error) {
      console.error('Failed to submit answer', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitText = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    handleAnswer(inputValue);
  };

  if (completed) {
    return (
        <div className="w-full h-[100svh] md:h-screen flex flex-col items-center justify-center p-8 text-left animate-fade-zoom-in">
            <div className="max-w-3xl w-full">
                <div className="flex items-center justify-center h-24 w-24 rounded-full bg-primary/10 mb-8">
                    <CheckCircle2 className="h-12 w-12 text-primary" />
                </div>
                <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Grazie!</h3>
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl leading-relaxed">
                    {finalMessage || "Abbiamo analizzato le tue risposte. Il tuo progetto ha un potenziale eccellente."}
                </p>
                <button className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-lg font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:bg-primary/90 active:scale-[0.98]">
                    Inizia il tuo progetto adesso
                </button>
            </div>
        </div>
    );
  }

  const hasStarted = messages.length > 0;

  return (
    <div className="w-full h-[100svh] md:h-screen max-h-[100svh] md:max-h-screen flex flex-col overflow-hidden relative">
      
      {/* Liquid Chrome Neon Background */}
      <LiquidChrome />
      
      {/* Dark Overlay for active conversation */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-1000 ease-in-out pointer-events-none ${
          hasStarted ? 'opacity-80' : 'opacity-0'
        }`} 
      />

      {/* ── Hero overlay ── */}
      {(OFFLINE_MODE || !hasStarted) && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col justify-end pb-16 px-6 lg:justify-center lg:pb-0 lg:px-14 xl:px-20">
          <div className="max-w-[680px]">

            {/* Eyebrow tag */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <span className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                <motion.span
                  className="h-1.5 w-1.5 rounded-full bg-primary"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                />
                Refineed Studio
              </span>
            </motion.div>

            {/* Word-by-word headline reveal — Emil: scale 0.97, not 0; y 18 not 40 */}
            <h1 className="text-[clamp(2.6rem,6vw,4.5rem)] font-black leading-[1.05] tracking-tight text-white">
              {headline.map((word, i) => (
                <motion.span
                  key={i}
                  className={`mr-[0.22em] inline-block${word.primary ? ' text-primary' : ''}`}
                  initial={{ opacity: 0, y: 18, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.55,
                    delay: 0.12 + i * 0.07,
                    ease: EASE,
                  }}
                >
                  {word.text}
                </motion.span>
              ))}
            </h1>

            {/* Subtext — max 20 words */}
            <motion.p
              className="mt-6 max-w-md text-base leading-7 text-gray-400 lg:text-[1.05rem] lg:leading-8"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.82, ease: EASE }}
            >
              Hai bisogno di vendere di più, perdere meno tempo, crescere senza inciampare nei processi.
              Il software è solo il mezzo. 
              Partiamo dal risultato che ti serve, e poi costruiamo lo strumento giusto.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="pointer-events-auto mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.95, ease: EASE }}
            >
              {/* Primary */}
              <Link
                href="/contatti"
                className="inline-flex items-center gap-2.5 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition-colors duration-150 hover:bg-primary/90 active:scale-[0.97]"
              >
                Parliamo subito
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>

              {/* Secondary */}
              <Link
                href="/#soluzioni"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-white"
              >
                Scopri alcune soluzioni
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
            </motion.div>

            {/* Trust strip */}
            <motion.p
              className="mt-5 text-xs text-gray-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.1, ease: EASE }}
            >
              Preventivo immediato · Risposta umana in 3 ore
            </motion.p>
          </div>
        </div>
      )}

      {/* Messages Area */}
      {!OFFLINE_MODE && (
      <div className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent px-4 md:px-0 transition-all duration-500 relative z-10 ${!hasStarted ? 'opacity-0' : 'opacity-100'}`} ref={scrollContainerRef}>
        <div className="w-full max-w-4xl mx-auto py-6 space-y-8 min-h-full flex flex-col justify-end">
            {messages.map((msg) => (
            <div
                key={msg.id}
                className={`flex w-full gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up-in`}
            >
                {msg.role === 'assistant' && (
                <div className="flex-shrink-0 mt-1">
                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center border border-white/20 backdrop-blur-md">
                    <Sparkles className="h-4 w-4 text-white" />
                    </div>
                </div>
                )}

                <div
                className={`
                    max-w-[85%] md:max-w-[75%] px-6 py-4 text-base leading-relaxed shadow-lg backdrop-blur-[80px]
                    ${msg.role === 'user'
                    ? 'bg-black/60 text-white rounded-3xl rounded-tr-sm border border-white/10' // User: Darker, high blur
                    : 'bg-[#d61e1e]/85 text-white rounded-3xl rounded-tl-sm border border-red-400/20 shadow-[0_4px_20px_rgba(220,38,38,0.4)]' // Assistant: Vibrant Red, high blur
                    }
                `}
                >
                {msg.content}
                </div>

                {msg.role === 'user' && (
                <div className="flex-shrink-0 mt-1">
                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center border border-white/10">
                    <User className="h-4 w-4 text-gray-300" />
                    </div>
                </div>
                )}
            </div>
            ))}
            
            {typing && (
            <div className="flex w-full gap-4 justify-start animate-slide-up-in">
                <div className="flex-shrink-0 mt-1">
                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center border border-white/20 backdrop-blur-md">
                    <Sparkles className="h-4 w-4 text-white" />
                    </div>
                </div>
                <div className="flex items-center gap-1 pl-1 pt-3">
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce anim-delay--300ms" />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce anim-delay--150ms" />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" />
                </div>
            </div>
            )}
        </div>
      </div>
      )}

      {/* Input Area */}
      {!OFFLINE_MODE && (
      <div className={`w-full z-20 bg-black/40 backdrop-blur-[40px] border-t border-white/10 transition-all duration-500 ${!hasStarted ? 'pt-6 pb-16' : 'py-4'}`}>
        <div className="w-full max-w-3xl mx-auto px-4">
            {error ? (
            <div className="flex flex-col items-center justify-center py-4 text-center">
                <p className="text-red-400 text-lg mb-4">{error}</p>
                <button 
                onClick={initChat}
                className="text-white text-base font-medium hover:underline"
                >
                Riprova
                </button>
            </div>
            ) : loading && !currentStep ? (
                <div className="h-16 flex items-center justify-center text-gray-300 text-lg animate-pulse gap-2">
                    <Sparkles className="h-5 w-5" />
                    <span>Inizializzazione...</span>
                </div>
            ) : !currentStep && !typing ? (
                <div className="h-16 flex items-center justify-center text-gray-300 text-lg animate-pulse gap-2">
                    <Sparkles className="h-5 w-5" />
                    <span>Thinking...</span>
                </div>
            ) : currentStep?.type === 'single_choice' ? (
            <div className="flex flex-wrap gap-3 animate-slide-up-in justify-center">
                {currentStep.options?.map((opt, idx) => (
                <button
                    key={opt.value}
                    onClick={() => handleAnswer(opt.value, opt.label)}
                    disabled={loading}
                    className="group relative inline-flex items-center justify-center rounded-2xl border border-white/10 bg-black/60 backdrop-blur-[80px] px-6 py-3 text-sm font-medium text-white hover:border-primary hover:text-white hover:bg-primary/20 transition-all hover:shadow-lg hover:-translate-y-0.5"
                    style={{ animationDelay: `${idx * 50}ms` }}
                >
                    <span className="mr-3 opacity-40 group-hover:opacity-100 transition-opacity text-sm font-mono">
                        {String.fromCharCode(65 + idx)}
                    </span>
                    {opt.label}
                </button>
                ))}
            </div>
            ) : (
            <form onSubmit={handleSubmitText} className="relative w-full animate-slide-up-in">
                <div className="relative flex items-center bg-black/60 backdrop-blur-[80px] rounded-[1.5rem] border border-white/10 focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/20 transition-all shadow-lg hover:border-white/20">
                <input
                    type={currentStep?.type === 'email' ? 'email' : 'text'}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={
                    // Use the question as placeholder for the first step if history is empty
                    (currentStep?.stepId === 'intro_context' && messages.length === 0 && currentStep.question) 
                        ? currentStep.question 
                        : (currentStep?.placeholder || "Scrivi un messaggio...")
                    }
                    disabled={loading || typing}
                    className="w-full bg-transparent py-4 pl-6 pr-14 text-base text-white placeholder:text-gray-400 focus:outline-none disabled:opacity-50"
                    autoFocus
                />
                <button
                    type="submit"
                    disabled={!inputValue.trim() || loading || typing}
                    className="absolute right-2 p-2 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-0 disabled:scale-90 transition-all duration-200 shadow-sm"
                >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </button>
                </div>
                <div className="text-center mt-4">
                <span className="text-xs text-gray-400">
                    Refineed AI
                </span>
                </div>
            </form>
            )}
        </div>
      </div>
      )}
    </div>
  );
}
